import { useCallback, useMemo } from 'react';
import type { MachineListRecord, MachineStatus, MachinesGateway } from '@forq/machines-data';
import type { ResolvedPeriod } from './period';
import { ReportActions, type SectionReport } from './report';
import { CustomizableMetrics } from '@forq/ui';
import { Bars, euro0, Kpi, KpiGrid, num, Panel, SectionShell, useAsyncData } from './shared';

const statusOrder: readonly MachineStatus[] = ['stock', 'rental', 'customer', 'sold', 'out-of-service'];
const statusLabels: Readonly<Record<MachineStatus, string>> = {
  stock: 'Bestand (verkaufbar)',
  rental: 'Mietpark',
  customer: 'Beim Kunden',
  sold: 'Verkauft',
  'out-of-service': 'Außer Betrieb',
};

/** Maschinen-Auswertung. Momentaufnahme: Bestand nach Status ist immer der aktuelle Stand. */
export function MachinesSection({ tenantId, gateway, period, tenantName }: {
  readonly tenantId: string;
  readonly gateway: MachinesGateway;
  readonly period: ResolvedPeriod;
  readonly tenantName: string;
}) {
  const load = useCallback(async (): Promise<readonly MachineListRecord[]> => {
    const page = await gateway.searchMachines({ tenantId, status: 'all', pageSize: 1000 });
    return page.items;
  }, [gateway, tenantId]);
  const { data, loading, error, reload } = useAsyncData(load);

  const summary = useMemo(() => {
    if (!data) return null;
    const counts = new Map<MachineStatus, number>();
    let stockValue = 0;
    let stockValueEk = 0;
    for (const machine of data) {
      counts.set(machine.status, (counts.get(machine.status) ?? 0) + 1);
      if (machine.status === 'stock') { stockValue += machine.salesPrice; stockValueEk += machine.purchasePrice; }
    }
    return { total: data.length, counts, stockValue, stockValueEk, reserve: stockValue - stockValueEk, stockCount: counts.get('stock') ?? 0 };
  }, [data]);

  const report: SectionReport | null = useMemo(() => {
    if (!summary) return null;
    return {
      title: 'Maschinen',
      kpis: [
        { label: 'Maschinen gesamt', value: num(summary.total) },
        { label: 'Bestand (verkaufbar)', value: num(summary.stockCount) },
        { label: 'Lagerwert (EK)', value: euro0(summary.stockValueEk) },
        { label: 'Lagerwert (VK)', value: euro0(summary.stockValue) },
        { label: 'Stille Reserve (VK−EK)', value: euro0(summary.reserve) },
        { label: 'Im Mietpark', value: num(summary.counts.get('rental') ?? 0) },
        { label: 'Verkauft', value: num(summary.counts.get('sold') ?? 0) },
      ],
      blocks: [
        { heading: 'Bestand nach Status', columns: ['Status', 'Anzahl'], rows: statusOrder.map((status) => [statusLabels[status], summary.counts.get(status) ?? 0]) },
      ],
    };
  }, [summary]);

  return (
    <SectionShell loading={loading} error={error} onReload={reload}>
      {summary && report ? (
        <div className="auswertung__stack">
          <div className="auswertung__sectionhead">
            <span className="sales-eyebrow">Maschinen · Momentaufnahme</span>
            <ReportActions report={report} period={period} tenantName={tenantName} />
          </div>
          <KpiGrid>
            <CustomizableMetrics
              pageKey="auswertung-maschinen"
              tiles={[
                { key: 'total', node: <Kpi label="Maschinen gesamt" value={num(summary.total)} hint="alle Status" /> },
                { key: 'stock', node: <Kpi label="Bestand (verkaufbar)" value={num(summary.stockCount)} hint="im Verkauf" /> },
                { key: 'value-ek', node: <Kpi label="Lagerwert (EK)" value={euro0(summary.stockValueEk)} hint="Einkaufspreise Bestand" /> },
                { key: 'value-vk', node: <Kpi label="Lagerwert (VK)" value={euro0(summary.stockValue)} hint="Verkaufspreise Bestand" /> },
                { key: 'reserve', node: <Kpi label="Stille Reserve" value={euro0(summary.reserve)} hint="VK − EK im Bestand" /> },
                { key: 'rental', node: <Kpi label="Im Mietpark" value={num(summary.counts.get('rental') ?? 0)} hint="als Mietgerät geführt" /> },
                { key: 'sold', node: <Kpi label="Verkauft" value={num(summary.counts.get('sold') ?? 0)} hint="abgeschlossen" /> },
              ]}
            />
          </KpiGrid>

          <Panel title="Bestand nach Status">
            <Bars
              rows={statusOrder.map((status) => ({ key: status, label: statusLabels[status], value: summary.counts.get(status) ?? 0 })).filter((row) => row.value > 0)}
              emptyLabel="Keine Maschinen im Stamm."
              format={num}
            />
          </Panel>
        </div>
      ) : null}
    </SectionShell>
  );
}
